import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@design-system/helpers';

const alertVariants = cva(
  'relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      type: {
        fill: '',
        outline: '',
      },
      variant: {
        default: '',
        destructive: '',
        warning: '',
      },
    },
    compoundVariants: [
      {
        type: 'fill',
        variant: 'default',
        class: 'bg-primary-50 border-0 text-gray-900',
      },
      {
        type: 'fill',
        variant: 'destructive',
        class:
          'bg-destructive/10 border-0 text-destructive [&>svg]:text-destructive *:data-[slot=alert-description]:text-destructive/90',
      },
      {
        type: 'fill',
        variant: 'warning',
        class:
          'bg-orange-50 border-0 text-orange-500 [&>svg]:text-orange-500 *:data-[slot=alert-description]:text-orange-500/90',
      },

      {
        type: 'outline',
        variant: 'default',
        class: 'bg-white border-pale-border text-gray-900',
      },
      {
        type: 'outline',
        variant: 'destructive',
        class:
          'bg-white border-destructive text-destructive [&>svg]:text-destructive *:data-[slot=alert-description]:text-destructive/90',
      },
      {
        type: 'outline',
        variant: 'warning',
        class:
          'bg-white text-orange-400 [&>svg]:text-orange-400 *:data-[slot=alert-description]:text-orange-400/90',
      },
    ],
    defaultVariants: {
      type: 'outline',
      variant: 'default',
    },
  }
);

function Alert({
  className,
  variant,
  type,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot='alert'
      role='alert'
      className={cn(alertVariants({ variant, type }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='alert-title'
      className={cn(
        'col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight',
        className
      )}
      {...props}
    />
  );
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='alert-description'
      className={cn(
        'text-gray-800 col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed',
        className
      )}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription };
